import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import noop from 'lodash/noop';
import Loader from '@adsk/bim360-matrix-react-loader';
import TreeSelection from '@adsk/bim360-matrix-react-tree-selection';
import EmptyState from '@adsk/bim360-matrix-react-empty-state';
import Button from '@adsk/bim360-matrix-react-button';
import { MDText } from 'i18n-react';
import folderIcon from '@adsk/bim360-matrix-react-assets/folder-icon.svg';
import projectFolderIcon from '@adsk/bim360-matrix-react-assets/folder-project-icon.svg';
import planFolderIcon from '@adsk/bim360-matrix-react-assets/folder-plans-icon.svg';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import { getIcon } from './helpers';

import './_DocumentSelectionTree.scss';

/**
 * Render Docs document selection tree
 */
export class DocumentSelectionTree extends Component {
  /**
   * DocumentSelectionTree component constructor.
   * @param props
   */
  constructor(props) {
    super(props);

    this.state = {
      rootNodes: null,
      loading: false,
      error: null,
      empty: false
    };

    this.fetchNode = this.fetchNode.bind(this);
    this.onLoadMore = this.onLoadMore.bind(this);
    this.getDocumentData = this.getDocumentData.bind(this);
    this.getFolderData = this.getFolderData.bind(this);
  }

  /**
   * componentWillReceiveProps
   */
  UNSAFE_componentWillMount() {
    this.fetchRootFolders();
  }

  /**
   * Fetch root folders
   */
  fetchRootFolders() {
    this.setState({ loading: true, error: null });
    this.props
      .getRootFolders()
      .then(data => {
        if (data.length === 0) {
          this.setState({ empty: true, loading: false });
        } else {
          this.setState({
            loading: false,
            rootNodes: data.map(folder => this.getFolderData(folder))
          });
        }
      })
      .catch(error => {
        this.setState({ error, loading: false });
      });
  }

  static getOriginalNodes(nodeArray = []) {
    return nodeArray.map(node => node.originalNode);
  }

  /**
   * fetchNode
   * @param node
   */
  fetchNode(node) {
    return this.fetchNodeContents(node.id);
  }

  onLoadMore({ parentNodes, availableChildNodes }) {
    return this.fetchNodeContents(parentNodes[0].id, {
      availableChildNodes: DocumentSelectionTree.getOriginalNodes(availableChildNodes)
    });
  }

  fetchNodeContents(nodeId, context) {
    return this.props.getFolderContents(nodeId, context).then(data => this.getFolderChildren(data));
  }

  /**
   * Generate folder node - returns an object representing `TreeSelection.treeSelectionNodeShape`.
   * @param folder
   */
  getFolderData(folder) {
    const { urn, title } = folder;
    const isProjectFolder = folder.docType === 'project.folder';
    let svg = folderIcon;
    if (isProjectFolder) {
      svg = folder.folderType === 'plan' ? planFolderIcon : projectFolderIcon;
    }
    const defaultHasMoreChildren = ({ parentNodes, availableChildNodes }) =>
      this.props.hasMoreFolderContents({
        folder,
        availableChildNodes: DocumentSelectionTree.getOriginalNodes(availableChildNodes)
      });

    return {
      id: urn,
      name: title,
      children: [],
      fetched: false,
      hasMoreChildren: folder[this.props.hasMoreFolderContentsKey] || defaultHasMoreChildren,
      type: DocumentSelectionTree.NODE_TYPES.FOLDER_TYPE,
      svg,
      originalNode: folder
    };
  }

  /**
   * Generate document node - returns an object representing `TreeSelection.treeSelectionNodeShape`.
   * @param document
   */
  getDocumentData(document) {
    const { urn, name, currentVersion } = document;
    return {
      id: urn,
      name: name || currentVersion.name,
      type: DocumentSelectionTree.NODE_TYPES.DOCUMENT_TYPE,
      svg: getIcon(document),
      originalNode: document
    };
  }

  /**
   * Get folder children
   * @param data
   */
  getFolderChildren(data) {
    return data.folders
      .map(folder => this.getFolderData(folder))
      .concat(data.documents.map(document => this.getDocumentData(document)));
  }

  /**
   * Render folders tree
   * @returns {XML}
   */
  renderTree() {
    return (
      <TreeSelection
        treeData={this.state.rootNodes}
        fetchNode={this.fetchNode}
        onLoadMore={this.onLoadMore}
        loadMoreText={this.props[TSymbolProp].translate('LOAD_MORE_FOLDER_CONTENTS')}
        onChange={this.props.onChange}
        errorLoadingNodeText={this.props[TSymbolProp].translate('ERROR_LOADING_FOLDER')}
        locale={this.props.locale}
        onlyLeafSelection={this.props.onlyLeafSelection}
        fixedSize={true}
        multi={this.props.multi}
        disabledNodesIds={this.props.disabledNodesIds}
        maxHeight={this.props.treeContainerHeight}
        dontOverrideHasMoreFolderContents={this.props.dontOverrideHasMoreFolderContents}
      />
    );
  }

  /**
   * Render error
   * @returns {XML}
   */
  renderError() {
    return (
      <EmptyState
        icon={EmptyState.Icons.CONE}
        className="DocumentSelectionTree__error"
        text={this.props[TSymbolProp].translate('ERROR_LOADING_ROOT_FOLDERS')}
        footer={
          <Button
            styleType={Button.StyleType.LINK}
            className="DocumentSelectionTree__error-try-again"
            onClick={() => this.fetchRootFolders()}
          >
            {this.props[TSymbolProp].translate('TRY_AGAIN')}
          </Button>
        }
      />
    );
  }

  renderEmpty() {
    return (
      <EmptyState
        icon={EmptyState.Icons.EMPTY_BOX}
        text={this.props[TSymbolProp].translate('EMPTY_ROOT_FOLDER')}
      />
    );
  }

  renderView() {
    const { error, loading, empty } = this.state;

    if (loading) {
      return <Loader />;
    } else if (error) {
      return this.renderError();
    } else if (empty) {
      return this.renderEmpty();
    }

    return this.renderTree();
  }

  /**
   * render
   * @returns {XML}
   */
  render() {
    const { className, style } = this.props;
    const classes = classnames('DocumentSelectionTree', className);

    return (
      <div className={classes} style={style}>
        {this.renderView()}
      </div>
    );
  }
}

DocumentSelectionTree.NODE_TYPES = {
  DOCUMENT_TYPE: 'DOCUMENT',
  FOLDER_TYPE: 'FOLDER'
};

DocumentSelectionTree.defaultProps = {
  onlyLeafSelection: true,
  hasMoreFolderContents: noop,
  hasMoreFolderContentsKey: 'hasNextPage',
  dontOverrideHasMoreFolderContents: false
};

DocumentSelectionTree.propTypes = {
  /**
   * Get Project's root folders.
   * Should return a promise that would get the data.
   */
  getRootFolders: PropTypes.func.isRequired,
  /**
   * Get folder's content.
   * Should return a promise that would get the data.
   * An optional context parameter will be present when getting the data using pagination.
   * The function is of the form:
   * (folderId :string, context :object?) => Promise<Array<Document>>
   *
   * Where context has property of availableChildNodes that contains only the nodes fetched so far for the current folder.
   * This would allow you to fetch the next page given you know where you are right now.
   */
  getFolderContents: PropTypes.func.isRequired,
  /**
   * An optional function that returns a boolean indicating whether a folder has more contents (for pagination).
   * The function is of the form:
   * ({ folder, availableChildNodes }) => bool
   *
   * folder is the parent folder object and availableChildNodes contains only the nodes fetched so far for the current folder.
   * This would allow you to determine if a folder has more contents or not based on what the user fetched so far.
   */
  hasMoreFolderContents: PropTypes.func,
  /**
   * In case each folder already contains the data of whether or not it still has more data to load and you don't want implement it using the function above,
   * You can pass this prop to indicate where in each folder the that value exists.
   */
  hasMoreFolderContentsKey: PropTypes.string,
  /**
   * Ensures that only the hasMoreFolderContents function is used to check whether there's more content to fetch (see onLoadMore in Tree.js)
   */
  dontOverrideHasMoreFolderContents: PropTypes.bool,
  /**
   * Handle document selection change.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * Style object
   */
  style: PropTypes.object,
  /**
   * Class name that's applied to the container
   */
  className: PropTypes.string,
  /**
   * The locale to use for this component
   */
  locale: PropTypes.string,
  /**
   * Permit selection of leaf nodes only
   */
  onlyLeafSelection: PropTypes.bool,
  /**
   * Permit multi selection on leaf nodes
   */
  multi: PropTypes.bool,
  /**
   * Disabled node IDs (only in multi selection mode)
   */
  disabledNodesIds: PropTypes.array,
  /**
   * Height for the Tree selection container
   */
  treeContainerHeight: PropTypes.number
};

export default withLocale(
  DocumentSelectionTree,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
